import { FormProvider, type FieldValues } from 'react-hook-form';
import type { ReactNode } from 'react';
import type { TLocalize } from '@hooks/use-localize';
import type { IUseStepFormReturn } from '@hooks/use-step-form';
import type { DashRegistrationStep } from '@/types/dashboard';
import type { IScreeningProgressModel } from '@/types/screening/screening-progress';
import type { IHistoryProgressModel } from '@/types/history/history-progress';
import ScreeningProgress from '@views/register/screening/screening-progress';
import HistoryProgress from '@views/register/history/history-progress';
import NavigationButtons from '@views/register/screening-history-navigation-buttons';
import RequiredFieldsAlert from '@components/required-fields-alert';

type TStepFormLayoutProps<TValues extends FieldValues> = {
    step: DashRegistrationStep;
    title: string;
    subtitle?: ReactNode;
    t: TLocalize;
    form: IUseStepFormReturn<TValues>;
    isSubmitting: boolean;
    submitError: string | null;
    contentClassName?: string;
    children: ReactNode;
} & (
    | { kind: 'screening'; model: IScreeningProgressModel }
    | { kind: 'history'; model: IHistoryProgressModel }
);

const StepFormLayout = <TValues extends FieldValues>(props: TStepFormLayoutProps<TValues>) => {
    const { step, title, subtitle, t, form, isSubmitting, submitError, children } = props;
    const isHistory = props.kind === 'history';
    const contentClassName = props.contentClassName ?? 'col-xs-12 col-sm-8 summary-mobile-content';
    const hasErrors = Object.keys(form.formState.errors).length > 0;

    return (
        <div className="row">
            {props.model.isIE && (
                <div className="alert alert-warning">
                    {t(
                        'Alert_BrowserRecommend',
                        'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                    )}
                    .
                </div>
            )}

            <div className={isHistory ? 'col-xs-12 col-sm-3 hidden-xs sticky-progress' : 'col-xs-12 col-sm-3 hidden-xs'}>
                {props.kind === 'screening' ? (
                    <ScreeningProgress model={props.model} overrideStep={step} />
                ) : (
                    <HistoryProgress model={props.model} overrideStep={step} />
                )}
            </div>

            <div className={contentClassName}>
                <div className={isHistory ? 'middle-content' : undefined}>
                    <RequiredFieldsAlert show={hasErrors} t={t} />

                    <h1>{title}</h1>

                    {subtitle}

                    <hr />

                    <FormProvider {...form}>
                        <form className="form-horzontal" onSubmit={form.onSubmit}>
                            <div className="row row-margined">
                                <div className="col-xs-12">
                                    {children}

                                    {submitError && (
                                        <div className="form-group col-xs-12">
                                            <div className="alert alert-danger">{submitError}</div>
                                        </div>
                                    )}

                                    <NavigationButtons
                                        registrationId={props.model.id}
                                        editKey={props.model.editKey}
                                        isFemale={props.model.isFemale}
                                        isLiver={props.model.isLiver}
                                        currentRegistrationStep={props.model.currentRegistrationStep}
                                        t={t}
                                        isSubmitting={isSubmitting}
                                        isScreening={!isHistory}
                                    />
                                </div>
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
};

export default StepFormLayout;
